import { createSelector } from 'reselect'
import * as R from 'ramda'
import { toHref } from 'helpers/PathHelper'
import { createBreadcrumb } from 'domain/scope'
import {
  findPrimaryNavigationNameInAncestors,
  findSubNavigationNameInAncestors,
  isNotPrimaryNavigation,
  isPrimaryNavigation,
  isTypeNavigation,
  isValidResource
} from 'domain/resources'
import { getResources } from 'store/reducers/resources'
import { Resource } from 'types/typescript'
import { getBreadcrumb, getNavItems, getScope } from './scope'
import { getOperationCode } from './application'
import { getQuery } from './router'
import { getCurrentResource } from './currentResource'
import {
  getHasOnlyOneUniverse,
  getNbUniversesInCatalog,
  getSaleResource
} from './sale'
import {
  GetCurrentResourceSubUniverseId,
  GetCurrentResourceSubUniverseName,
  GetCurrentResourceSubUniverseNameExcludingAllProducts,
  GetCurrentResourceUniverseId,
  GetCurrentResourceUniverseName,
  GetCurrentResourceUniverseNameExcludingAllProducts,
  GetIsFirstUniverse,
  GetSaleHomePageUrl,
  GetSubUniverseName,
  GetSubUniverseNameExcludingAllProducts,
  GetUniverseName,
  GetUniverseNameExcludingAllProducts
} from './universes.types'

export const getCurrentResourceUniverseName: GetCurrentResourceUniverseName = createSelector(
  getBreadcrumb,
  findPrimaryNavigationNameInAncestors
)

export const getCurrentResourceUniverseNameExcludingAllProducts: GetCurrentResourceUniverseNameExcludingAllProducts = createSelector(
  getResources,
  getCurrentResource,
  getScope,
  getSaleResource,
  getHasOnlyOneUniverse,
  getOperationCode,
  (allResources, currentResource, scope, sale, saleHasOnlyOneUniverse, opeCode) => (isValidResource(currentResource))
    ? findPrimaryNavigationNameInAncestors(createBreadcrumb(currentResource as Resource, saleHasOnlyOneUniverse, scope, sale, allResources, opeCode, false))
    : []
)

export const getCurrentResourceUniverseId: GetCurrentResourceUniverseId = createSelector(
  getBreadcrumb,
  R.ifElse(
    R.isNil,
    R.always(''),
    R.compose(
      R.prop('key'),
      R.defaultTo({}),
      R.find(isPrimaryNavigation),
      R.reverse
    )
  )
)

export const getCurrentResourceSubUniverseName: GetCurrentResourceSubUniverseName = createSelector(
  getBreadcrumb,
  findSubNavigationNameInAncestors
)

export const getCurrentResourceSubUniverseNameExcludingAllProducts: GetCurrentResourceSubUniverseNameExcludingAllProducts = createSelector(
  getResources,
  getCurrentResource,
  getScope,
  getSaleResource,
  getHasOnlyOneUniverse,
  getOperationCode,
  (allResources, currentResource, scope, sale, saleHasOnlyOneUniverse, opeCode) => (isValidResource(currentResource))
    ? findSubNavigationNameInAncestors(createBreadcrumb(currentResource as Resource, saleHasOnlyOneUniverse, scope, sale, allResources, opeCode, false))
    : []
)

export const getCurrentResourceSubUniverseId: GetCurrentResourceSubUniverseId = createSelector(
  getBreadcrumb,
  R.ifElse(
    R.isNil,
    R.always(''),
    R.compose(
      R.prop('key'),
      R.defaultTo({}),
      R.find(R.both(isNotPrimaryNavigation, isTypeNavigation)),
      R.reverse
    )
  )
)

export const getSaleHomePageUrl: GetSaleHomePageUrl = createSelector(
  getOperationCode,
  getQuery,
  (opeCode, query) => toHref({ pathname: `/${opeCode}/saleHome` }, query)
)

export const getIsFirstUniverse: GetIsFirstUniverse = createSelector(
  getNavItems,
  getCurrentResourceUniverseId,
  (universes, currentUniverseId) => (
    R.pipe(
      R.head,
      R.prop('key'),
      R.equals(currentUniverseId)
    )(universes)
  )
)

const getCurrentResourceName = (
  nbUniverses: number,
  name: string
): string => R.ifElse(
  R.lt(1),
  R.always(name),
  R.always('')
)(nbUniverses)

export const getUniverseName: GetUniverseName = createSelector(
  getNbUniversesInCatalog,
  getCurrentResourceUniverseName,
  (nbUniverses, universeName) => getCurrentResourceName(nbUniverses, universeName)
)

export const getUniverseNameExcludingAllProducts: GetUniverseNameExcludingAllProducts = createSelector(
  getNbUniversesInCatalog,
  getCurrentResourceUniverseNameExcludingAllProducts,
  (nbUniverses, universeName) => getCurrentResourceName(nbUniverses, universeName as string)
)

export const getSubUniverseName: GetSubUniverseName = createSelector(
  getNbUniversesInCatalog,
  getCurrentResourceSubUniverseName,
  (nbUniverses, universeName) => getCurrentResourceName(nbUniverses, universeName)
)

export const getSubUniverseNameExcludingAllProducts: GetSubUniverseNameExcludingAllProducts = createSelector(
  getNbUniversesInCatalog,
  getCurrentResourceSubUniverseNameExcludingAllProducts,
  (nbUniverses, universeName) => getCurrentResourceName(nbUniverses, universeName as string)
)
